/**
 * HistoryScreen
 * View completion history with calendar and statistics
 */

import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  ActivityIndicator,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { HistoryScreenProps } from '../navigation/types';
import { useGoals } from '../hooks/useGoals';
import { useCompletions } from '../hooks/useCompletions';
import { getLastNDays, formatDateForDisplay } from '../utils/dateUtils';

export const HistoryScreen: React.FC<HistoryScreenProps> = ({ route, navigation }) => {
  const { goalId } = route.params;
  const { findGoalById, currentAccountId } = useGoals();
  const {
    isCompleted,
    toggleCompletion,
    getCurrentStreak,
    getLongestStreak,
    getTotalCompletions,
    getCompletionRate,
    getCompletionDates,
    loadCompletionsForDateRange,
  } = useCompletions();

  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
  const [selectedDate, setSelectedDate] = useState(new Date());

  const goal = findGoalById(goalId);

  const handleToggleDay = async (date: string) => {
    if (!currentAccountId) return;
    await toggleCompletion(currentAccountId, goalId, date);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setSelectedDate(newDate);
  };

  const navigateYear = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setFullYear(newDate.getFullYear() - 1);
    } else {
      newDate.setFullYear(newDate.getFullYear() + 1);
    }
    setSelectedDate(newDate);
  };

  // Build calendar for a single month
  interface MonthData {
    year: number;
    month: number;
    monthName: string;
    weeks: (string | null)[][];
  }

  const buildMonthCalendar = (year: number, month: number): MonthData => {
    const firstDay = new Date(year, month, 1);
    const monthName = firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const calendarDays: (string | null)[] = [];

    // Empty cells before month starts
    for (let i = 0; i < startDayOfWeek; i++) {
      calendarDays.push(null);
    }

    // All days in month - create dates using local time
    for (let day = 1; day <= daysInMonth; day++) {
      // Format as YYYY-MM-DD in local timezone
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      calendarDays.push(dateStr);
    }

    // Pad the end to complete the last week (make it a multiple of 7)
    const remainingCells = (7 - (calendarDays.length % 7)) % 7;
    for (let i = 0; i < remainingCells; i++) {
      calendarDays.push(null);
    }

    // Group into weeks
    const weeks: (string | null)[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    return { year, month, monthName, weeks };
  };

  // Get calendar data based on view mode
  const getCalendarData = (): MonthData[] => {
    if (viewMode === 'month') {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth();
      return [buildMonthCalendar(year, month)];
    } else {
      // Year view - show all 12 months
      const year = selectedDate.getFullYear();
      const months: MonthData[] = [];
      for (let month = 0; month < 12; month++) {
        months.push(buildMonthCalendar(year, month));
      }
      return months;
    }
  };

  const calendarData = getCalendarData();

  useEffect(() => {
    if (currentAccountId && goalId) {
      // Always load last 365 days for accurate stats, regardless of view
      const allDates = getLastNDays(365);

      loadCompletionsForDateRange(currentAccountId, goalId, allDates).then(() => {
        setIsLoading(false);
      });
    }
  }, [currentAccountId, goalId]);

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
        </View>
      </SafeAreaView>
    );
  }

  if (!goal) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Goal not found</Text>
        </View>
      </SafeAreaView>
    );
  }

  const currentStreak = getCurrentStreak(goalId);
  const longestStreak = getLongestStreak(goalId);
  const totalCompletions = getTotalCompletions(goalId);
  const last7DaysRate = getCompletionRate(goalId, 7);
  const last30DaysRate = getCompletionRate(goalId, 30);
  const last90DaysRate = getCompletionRate(goalId, 90);

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.goalName}>{goal.name}</Text>
          <Text style={styles.goalType}>
            {goal.type === 'main' ? 'Main Goal' : 'Side Goal'}
          </Text>
        </View>

        {/* Statistics */}
        <View style={styles.statsSection}>
          <Text style={styles.sectionTitle}>Statistics</Text>

          <View style={styles.statsGrid}>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{currentStreak}</Text>
              <Text style={styles.statLabel}>Current Streak</Text>
            </View>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{longestStreak}</Text>
              <Text style={styles.statLabel}>Longest Streak</Text>
            </View>
            <View style={styles.statCard}>
              <Text style={styles.statValue}>{totalCompletions}</Text>
              <Text style={styles.statLabel}>Total Days</Text>
            </View>
          </View>

          <View style={styles.ratesSection}>
            <View style={styles.rateRow}>
              <Text style={styles.rateLabel}>Last 7 days</Text>
              <Text style={styles.rateValue}>{last7DaysRate.toFixed(0)}%</Text>
            </View>
            <View style={styles.rateRow}>
              <Text style={styles.rateLabel}>Last 30 days</Text>
              <Text style={styles.rateValue}>{last30DaysRate.toFixed(0)}%</Text>
            </View>
            <View style={styles.rateRow}>
              <Text style={styles.rateLabel}>Last 90 days</Text>
              <Text style={styles.rateValue}>{last90DaysRate.toFixed(0)}%</Text>
            </View>
          </View>
        </View>

        {/* View Mode Toggle */}
        <View style={styles.viewToggle}>
          <TouchableOpacity
            style={[styles.toggleButton, viewMode === 'month' && styles.toggleButtonActive]}
            onPress={() => setViewMode('month')}
          >
            <Text style={[styles.toggleText, viewMode === 'month' && styles.toggleTextActive]}>
              Month
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toggleButton, viewMode === 'year' && styles.toggleButtonActive]}
            onPress={() => setViewMode('year')}
          >
            <Text style={[styles.toggleText, viewMode === 'year' && styles.toggleTextActive]}>
              Year
            </Text>
          </TouchableOpacity>
        </View>

        {/* Navigation */}
        <View style={styles.navigation}>
          <TouchableOpacity
            style={styles.navButton}
            onPress={() => viewMode === 'month' ? navigateMonth('prev') : navigateYear('prev')}
          >
            <Text style={styles.navButtonText}>←</Text>
          </TouchableOpacity>
          <Text style={styles.navTitle}>
            {viewMode === 'month'
              ? selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
              : selectedDate.getFullYear()}
          </Text>
          <TouchableOpacity
            style={styles.navButton}
            onPress={() => viewMode === 'month' ? navigateMonth('next') : navigateYear('next')}
          >
            <Text style={styles.navButtonText}>→</Text>
          </TouchableOpacity>
        </View>

        {/* Calendar View */}
        <View style={styles.calendarSection}>
          <Text style={styles.instructionText}>Tap any day to mark it complete or incomplete</Text>

          <View style={viewMode === 'year' ? styles.yearGrid : undefined}>
            {calendarData.map((monthData, monthIndex) => (
            <View key={`${monthData.year}-${monthData.month}`} style={styles.monthContainer}>
              <Text style={styles.monthHeader}>{monthData.monthName}</Text>

              {/* Day of week headers */}
              <View style={styles.weekRow}>
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                  <View key={i} style={styles.dayHeader}>
                    <Text style={styles.dayHeaderText}>{day}</Text>
                  </View>
                ))}
              </View>

              {/* Calendar grid */}
              {monthData.weeks.map((week, weekIndex) => (
                <View key={weekIndex} style={styles.weekRow}>
                  {week.map((date, dayIndex) => {
                    if (!date) {
                      return <View key={`empty-${weekIndex}-${dayIndex}`} style={styles.dayCellEmpty} />;
                    }

                    const completed = isCompleted(goalId, date);
                    // Parse date as local time to avoid timezone issues
                    const [y, m, d] = date.split('-').map(Number);
                    const dayNumber = d;

                    return (
                      <TouchableOpacity
                        key={date}
                        style={[
                          styles.dayCell,
                          completed && styles.dayCellCompleted,
                        ]}
                        onPress={() => handleToggleDay(date)}
                        accessibilityLabel={`${formatDateForDisplay(date)}, ${completed ? 'completed' : 'incomplete'}`}
                        accessibilityHint="Tap to toggle completion"
                      >
                        <Text style={[styles.dayText, completed && styles.dayTextCompleted]}>
                          {dayNumber}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ))}
            </View>
            ))}
          </View>

          <View style={styles.legend}>
            <View style={styles.legendItem}>
              <View style={[styles.legendBox, styles.dayCellCompleted]} />
              <Text style={styles.legendText}>Completed</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={styles.legendBox} />
              <Text style={styles.legendText}>Incomplete</Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    fontSize: 16,
    color: '#666',
  },
  header: {
    backgroundColor: '#fff',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  goalName: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  goalType: {
    fontSize: 14,
    color: '#666',
  },
  statsSection: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  instructionText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
    fontStyle: 'italic',
  },
  statsGrid: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  statCard: {
    flex: 1,
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginHorizontal: 6,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    }),
  },
  statValue: {
    fontSize: 28,
    fontWeight: '700',
    color: '#007AFF',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
  ratesSection: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    }),
  },
  rateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  rateLabel: {
    fontSize: 14,
    color: '#666',
  },
  rateValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
  viewToggle: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    marginBottom: 12,
    justifyContent: 'center',
  },
  toggleButton: {
    flex: 1,
    maxWidth: 120,
    backgroundColor: '#fff',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    marginHorizontal: 4,
  },
  toggleButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  toggleText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  toggleTextActive: {
    color: '#fff',
  },
  navigation: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  navButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  navButtonText: {
    fontSize: 24,
    color: '#007AFF',
  },
  navTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  calendarSection: {
    padding: 16,
  },
  yearGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  monthContainer: {
    marginBottom: 16,
    width: '100%',
  },
  monthHeader: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  weekRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  dayHeader: {
    flex: 1,
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dayHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#999',
  },
  dayCell: {
    flex: 1,
    aspectRatio: 1,
    backgroundColor: '#fff',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#eee',
  },
  dayCellEmpty: {
    flex: 1,
    aspectRatio: 1,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  dayCellCompleted: {
    backgroundColor: '#34C759',
    borderColor: '#34C759',
  },
  dayText: {
    fontSize: 12,
    color: '#666',
  },
  dayTextCompleted: {
    color: '#fff',
    fontWeight: '600',
  },
  legend: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 12,
  },
  legendBox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#eee',
    marginRight: 8,
  },
  legendText: {
    fontSize: 12,
    color: '#666',
  },
});
